import { FormPage } from '@/components/forms/FormPage';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import type { CryptoAsset, CryptoNetwork, DestinationType } from '@/features/user/wallet';
import { AccountSelectionCard } from '@/features/user/wallet/AccountSelectionCard';
import { DestinationTypeTabs } from '@/features/user/wallet/DestinationTypeTabs';
import { NewWalletEntry } from '@/features/user/wallet/ExternalWalletCard';
import {
  ALL_STABLECOINS,
  EUR_PEGGED_STABLECOINS,
  USD_PEGGED_STABLECOINS,
  useWithdrawableBalance,
} from '@/features/user/wallet/hooks/useExchangeRates';
import { getEstimatedArrival, getWithdrawalMinimum, useFeeCalculation } from '@/features/user/wallet/hooks/useFeeCalculation';
import {
  BridgeWallet,
  ExternalAccount,
  useExternalAccounts,
  useFiatWithdrawal,
  useWalletBalances,
  useWalletTransfer
} from '@/features/user/wallet/hooks/useWallet';
import { NetworkAssetSelector, getNetworkInfo } from '@/features/user/wallet/NetworkAssetSelector';
import { PaymentRailCard } from '@/features/user/wallet/PaymentRailCard';
import { WithdrawSummaryCard, WithdrawSummaryInline } from '@/features/user/wallet/WithdrawSummaryCard';
// Removed unused: useProfileData
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { toast } from '@/lib/sonner-adapter';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState } from '@supabank/ui';
import {
  AlertTriangle,
  ArrowDown,
  Coins,
  CreditCard,
  Loader2,
  Shield,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================
// TYPES & CONSTANTS
// ============================================

type FiatCurrency = 'USD' | 'EUR';
type PaymentRail = 'ach' | 'wire' | 'sepa';

// Stablecoin constants imported from useExchangeRates hook
// USD_PEGGED_STABLECOINS: USDC, USDT, USDB, DAI, PYUSD, USDP
// EUR_PEGGED_STABLECOINS: EURC

const PAYMENT_RAILS: Record<FiatCurrency, PaymentRail[]> = {
  USD: ['ach', 'wire'],
  EUR: ['sepa'],
};

const PAYMENT_RAIL_LABELS: Record<PaymentRail, { name: string; description: string; eta: string }> = {
  ach: { name: 'ACH', description: 'Automated Clearing House (US only)', eta: '1-3 business days' },
  wire: { name: 'Wire Transfer', description: 'Same-day transfer (US)', eta: 'Same day' },
  sepa: { name: 'SEPA', description: 'Single Euro Payments Area (EUR)', eta: '1-2 business days' },
};

const CURRENCY_OPTIONS = [
  { id: 'USD' as const, name: 'US Dollar', symbol: '$' },
  { id: 'EUR' as const, name: 'Euro', symbol: '€' },
];

// ============================================
// STEP INDICATOR COMPONENT
// ============================================

interface StepIndicatorProps {
  steps: string[];
  currentStep: number;
}

function StepIndicator({ steps, currentStep }: StepIndicatorProps) {
  return (
    <nav aria-label="Withdrawal progress" className="mb-8">
      <ol className="flex items-center justify-center gap-2 sm:gap-4">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;

          const stepStateClass = isCompleted
            ? "bg-primary text-primary-foreground"
            : isCurrent
              ? "bg-primary/20 text-primary ring-2 ring-primary"
              : "bg-muted text-muted-foreground";

          return (
            <li key={step} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center",
                  semanticTokens.motion.standard,
                  stepStateClass
                )}>
                  {isCompleted ? (
                    <SFIcon icon={sfIcons.check} className="w-4 h-4" />
                  ) : (
                    <span className={cn(semanticTokens.typography.label)}>{index + 1}</span>
                  )}
                </div>
                <span className={cn(
                  "mt-1.5 text-xs hidden sm:block",
                  isCurrent ? semanticTokens.fg.default : semanticTokens.fg.muted
                )}>
                  {step}
                </span>
              </div>

              {index < steps.length - 1 && (
                <div className={cn(
                  "w-8 sm:w-12 h-0.5 mx-1 sm:mx-2",
                  isCompleted ? "bg-primary" : "bg-muted"
                )} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ============================================
// BALANCE BREAKDOWN COMPONENT (WITH BRIDGE RATES)
// ============================================

interface BalanceBreakdownProps {
  breakdown: Array<{
    currency: string;
    amount: number;
    convertedAmount: number;
    rate: number;
  }>;
  targetCurrency: FiatCurrency;
  total: number;
  rates: {
    usdToEur: number;
    eurToUsd: number;
  };
  isLoading?: boolean;
}

function BalanceBreakdown({ breakdown, targetCurrency, total, rates, isLoading }: BalanceBreakdownProps) {
  const symbol = targetCurrency === 'USD' ? '$' : '€';

  if (isLoading) {
    return (
      <div className={cn(
        "p-3 space-y-2",
        semanticTokens.bg.muted,
        semanticTokens.radius.lg
      )}>
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (breakdown.length === 0) {
    return (
      <div className={cn(
        "p-3",
        semanticTokens.warning.bg,
        semanticTokens.warning.border,
        semanticTokens.radius.lg,
        "border"
      )}>
        <div className="flex items-center gap-2">
          <AlertTriangle className={cn("w-4 h-4", semanticTokens.warning.accent)} />
          <span className={cn(semanticTokens.typography.body, semanticTokens.warning.fg)}>
            No stablecoins available for {targetCurrency} withdrawal
          </span>
        </div>
        <p className={cn("mt-1", semanticTokens.typography.caption, semanticTokens.fg.muted)}>
          Deposit USDC, USDT, EURC or other stablecoins to withdraw
        </p>
      </div>
    );
  }

  return (
    <div className={cn(
      "p-3 space-y-3",
      semanticTokens.bg.muted,
      semanticTokens.radius.lg
    )}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className={cn("w-4 h-4", semanticTokens.fg.muted)} />
          <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
            Available for {targetCurrency} withdrawal
          </span>
        </div>
        <span className={cn(semanticTokens.typography.h4, semanticTokens.fg.default)}>
          {symbol}{total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {/* Breakdown by stablecoin */}
      <div className="space-y-1.5">
        {breakdown.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <CurrencyIcon symbol={item.currency} size="xs" />
              <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                {item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currency}
              </span>
              {item.rate !== 1 && (
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  semanticTokens.info.bg,
                  semanticTokens.info.fg
                )}>
                  ×{item.rate.toFixed(4)}
                </span>
              )}
            </div>
            <span className={cn(semanticTokens.typography.body, semanticTokens.fg.default)}>
              {symbol}{item.convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        ))}
      </div>

      {/* Exchange rate info */}
      {targetCurrency === 'EUR' && breakdown.some(b => USD_PEGGED_STABLECOINS.includes(b.currency as typeof USD_PEGGED_STABLECOINS[number])) && (
        <div className={cn(
          "flex items-center gap-2 pt-2 border-t",
          semanticTokens.border.default
        )}>
          <Info className={cn("w-3 h-3", semanticTokens.info.accent)} />
          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
            Rate: 1 USD = {rates.usdToEur.toFixed(4)} EUR
          </span>
        </div>
      )}
      {targetCurrency === 'USD' && breakdown.some(b => EUR_PEGGED_STABLECOINS.includes(b.currency as typeof EUR_PEGGED_STABLECOINS[number])) && (
        <div className={cn(
          "flex items-center gap-2 pt-2 border-t",
          semanticTokens.border.default
        )}>
          <Info className={cn("w-3 h-3", semanticTokens.info.accent)} />
          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
            Rate: 1 EUR = {rates.eurToUsd.toFixed(4)} USD
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================
// AMOUNT INPUT COMPONENT
// ============================================

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  currency: string;
  maxAmount: number;
  minAmount?: number;
  disabled?: boolean;
}

function AmountInput({
  value,
  onChange,
  currency,
  maxAmount,
  minAmount = 10,
  disabled = false
}: AmountInputProps) {
  const symbol = ['USD', 'USDC'].includes(currency) ? '$' : currency === 'EUR' ? '€' : '';
  const numValue = parseFloat(value) || 0;
  const isOverMax = numValue > maxAmount;
  const isUnderMin = numValue > 0 && numValue < minAmount;
  const hasError = isOverMax || isUnderMin;

  const handleMaxClick = () => {
    onChange(maxAmount.toFixed(2));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="withdraw-amount" className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
          Amount
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleMaxClick}
          disabled={disabled || maxAmount <= 0}
          className={cn(
            "h-7 px-2 text-xs font-medium",
            semanticTokens.motion.standard
          )}
        >
          <TrendingUp className="w-3 h-3 mr-1" />
          Max
        </Button>
      </div>

      <div className="relative">
        <span className={cn(
          "absolute left-4 top-1/2 -translate-y-1/2",
          "text-2xl font-semibold",
          semanticTokens.fg.muted
        )}>
          {symbol}
        </span>
        <Input
          id="withdraw-amount"
          type="number"
          placeholder="0.00"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={minAmount}
          max={maxAmount}
          step="0.01"
          disabled={disabled}
          className={cn(
            "font-display text-display-sm h-16 pl-12 pr-20",
            semanticTokens.motion.standard,
            semanticTokens.focus.ring,
            hasError && "border-red-500 focus:ring-red-500",
            semanticTokens.fg.default
          )}
          aria-label={`Withdrawal amount in ${currency}`}
          aria-invalid={hasError}
        />
        <div className={cn(
          "absolute right-4 top-1/2 -translate-y-1/2",
          "flex items-center gap-1.5"
        )}>
          <CurrencyIcon symbol={currency} size="sm" />
          <span className={cn(semanticTokens.typography.label, semanticTokens.fg.muted)}>
            {currency}
          </span>
        </div>
      </div>

      {/* Balance indicator */}
      <div className="flex items-center justify-between">
        <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
          Available: {symbol}{maxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        {hasError && (
          <span className={cn(semanticTokens.typography.caption, semanticTokens.danger.fg)}>
            {isOverMax ? 'Exceeds available balance' : `Minimum ${symbol}${minAmount}`}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN WITHDRAW PAGE COMPONENT
// ============================================

const STEPS = ['Type', 'Destination', 'Amount', 'Review'];

export default function WithdrawPage() {
  const navigate = useNavigate();
  const { data: externalAccounts = [], isLoading: isLoadingAccounts } = useExternalAccounts();
  const { data: walletBalances = [] } = useWalletBalances();
  const withdrawMutation = useFiatWithdrawal();
  const cryptoTransferMutation = useWalletTransfer();

  // Step tracking
  const [currentStep, setCurrentStep] = useState(0);

  // Destination type
  const [destinationType, setDestinationType] = useState<DestinationType>('bank');

  // Bank withdrawal state
  const [selectedCurrency, setSelectedCurrency] = useState<FiatCurrency>('USD');
  const [selectedPaymentRail, setSelectedPaymentRail] = useState<PaymentRail>('ach');
  const [selectedAccount, setSelectedAccount] = useState<ExternalAccount | null>(null);

  // Crypto withdrawal state
  const [selectedNetwork, setSelectedNetwork] = useState<CryptoNetwork | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<CryptoAsset | null>(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [stellarMemo, setStellarMemo] = useState('');
  const [stellarMemoError, setStellarMemoError] = useState<string | null>(null);

  // Amount
  const [amount, setAmount] = useState('');

  // Optional reference for bank withdrawals (recipient will see in bank statement)
  const [bankReference, setBankReference] = useState('');

  // ============================================
  // BALANCE CALCULATIONS (WITH BRIDGE API RATES)
  // ============================================

  // Flatten all stablecoin balances from all wallets
  const stablecoinBalances = useMemo(() => {
    if (!walletBalances || !Array.isArray(walletBalances)) return [];

    return walletBalances.flatMap(wallet => {
      if (!wallet?.balances || !Array.isArray(wallet.balances)) return [];
      
      return wallet.balances
        .filter(b => ALL_STABLECOINS.includes(b.currency.toUpperCase() as typeof ALL_STABLECOINS[number]))
        .map(balance => ({
          currency: balance.currency.toUpperCase(),
          amount: parseFloat(balance.balance || '0'),
          chain: wallet.chain,
        }));
    });
  }, [walletBalances]);

  // Use Bridge API exchange rates for fiat withdrawal calculation
  // This converts ALL stablecoins to target fiat using real-time rates
  const {
    total: availableBalanceForFiat,
    breakdown: fiatBreakdown,
    isLoading: isLoadingRates,
    rates
  } = useWithdrawableBalance(stablecoinBalances, selectedCurrency);

  // For CRYPTO WITHDRAWAL: Balance of specific asset on specific network
  const availableBalanceForCrypto = useMemo(() => {
    if (!selectedNetwork || !selectedAsset) return 0;

    const matchingBalance = stablecoinBalances.find(b =>
      b.currency === selectedAsset &&
      b.chain?.toLowerCase() === selectedNetwork
    );

    return matchingBalance?.amount || 0;
  }, [stablecoinBalances, selectedNetwork, selectedAsset]);

  // Get the appropriate balance based on destination type
  const availableBalance = destinationType === 'bank'
    ? availableBalanceForFiat
    : availableBalanceForCrypto;

  // Calculate fees based on current selections
  const fees = useFeeCalculation({
    amount: parseFloat(amount) || 0,
    currency: destinationType === 'bank' ? selectedCurrency : (selectedAsset || 'USDC'),
    destinationType,
    paymentRail: destinationType === 'bank' ? selectedPaymentRail : undefined,
    network: selectedNetwork || undefined,
    exchangeRate: rates?.usdToEur || 1,
  });

  // Calculate minimum withdrawal amount based on current selections
  const minimumWithdrawAmount = useMemo(() => {
    return getWithdrawalMinimum({
      destinationType,
      currency: destinationType === 'bank' ? selectedCurrency : undefined,
      asset: selectedAsset || undefined,
      network: selectedNetwork || undefined,
    });
  }, [destinationType, selectedCurrency, selectedAsset, selectedNetwork]);

  // Filter external accounts by selected currency
  const filteredAccounts = useMemo((): ExternalAccount[] => {
    if (!externalAccounts || !Array.isArray(externalAccounts) || externalAccounts.length === 0) return [];
    return externalAccounts.filter((acc: ExternalAccount) => {
      const accCurrency = acc.currency?.toUpperCase();
      if (accCurrency !== selectedCurrency) return false;
      if (selectedCurrency === 'USD' && acc.account_type?.toLowerCase() !== 'us') return false;
      if (selectedCurrency === 'EUR' && acc.account_type?.toLowerCase() !== 'iban') return false;
      return acc.status === 'active';
    });
  }, [externalAccounts, selectedCurrency]);

  // Reset selected account when currency changes
  useEffect(() => {
    if (selectedAccount && !filteredAccounts.find((a: ExternalAccount) => a.id === selectedAccount.id)) {
      setSelectedAccount(null);
    }
  }, [selectedCurrency, filteredAccounts, selectedAccount]);

  // Update payment rail when currency changes
  useEffect(() => {
    const railsForCurrency = PAYMENT_RAILS[selectedCurrency];
    if (railsForCurrency && railsForCurrency.length > 0 && !railsForCurrency.includes(selectedPaymentRail)) {
      setSelectedPaymentRail(railsForCurrency[0] as PaymentRail);
    }
  }, [selectedCurrency, selectedPaymentRail]);

  // Address validation
  useEffect(() => {
    if (!walletAddress || !selectedNetwork) {
      setIsAddressValid(false);
      return;
    }

    const addr = walletAddress.trim();
    const isValid = (() => {
      switch (selectedNetwork) {
        case 'ethereum':
        case 'polygon':
        case 'arbitrum':
        case 'optimism':
        case 'base':
        case 'avalanche':
          return /^0x[a-fA-F0-9]{40}$/.test(addr);
        case 'solana':
          return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
        case 'tron':
          return /^T[a-zA-Z0-9]{33}$/.test(addr);
        case 'stellar':
          return /^G[A-Z2-7]{55}$/.test(addr);
        default:
          return addr.length > 10;
      }
    })();

    setIsAddressValid(isValid);
  }, [walletAddress, selectedNetwork]);

  // Validate Stellar memo
  useEffect(() => {
    if (selectedNetwork !== 'stellar') {
      setStellarMemoError(null);
      return;
    }

    if (!stellarMemo.trim()) {
      setStellarMemoError('Memo is required for Stellar network');
      return;
    }

    if (stellarMemo.length > 28) {
      setStellarMemoError('Memo must be 28 characters or less');
      return;
    }

    setStellarMemoError(null);
  }, [stellarMemo, selectedNetwork]);

  // Reset Stellar memo when network changes
  useEffect(() => {
    if (selectedNetwork !== 'stellar') {
      setStellarMemo('');
      setStellarMemoError(null);
    }
  }, [selectedNetwork]);

  // Reset form when destination type changes
  useEffect(() => {
    setCurrentStep(0);
    setAmount('');
    if (destinationType === 'bank') {
      setSelectedNetwork(null);
      setSelectedAsset(null);
      setWalletAddress('');
    } else {
      setSelectedAccount(null);
    }
  }, [destinationType]);

  // Step validity
  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0: return true;
      case 1:
        if (destinationType === 'bank') {
          return selectedAccount !== null;
        } else {
          const baseValid = selectedNetwork !== null && selectedAsset !== null && isAddressValid;
          // For Stellar: require valid memo
          if (selectedNetwork === 'stellar') {
            return baseValid && stellarMemo.trim().length > 0 && stellarMemo.length <= 28;
          }
          return baseValid;
        }
      case 2: {
        const numAmount = parseFloat(amount);
        return !isNaN(numAmount) && numAmount >= minimumWithdrawAmount && numAmount <= availableBalance;
      }
      case 3: return true;
      default: return false;
    }
  }, [destinationType, selectedAccount, selectedNetwork, selectedAsset, isAddressValid, stellarMemo, amount, availableBalance, minimumWithdrawAmount]);

  // Navigation
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && isStepValid(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Withdrawal handler
  const handleWithdraw = async () => {
    if (destinationType === 'bank') {
      if (!selectedAccount || !amount || !selectedPaymentRail) {
        toast.error('Please fill all required fields');
        return;
      }

      // Find wallet and determine best source stablecoin
      // Priority: For EUR → EURC first, then USD stablecoins
      //           For USD → USD stablecoins (USDC preferred)
      const stablecoins: readonly string[] = selectedCurrency === 'USD'
        ? [...USD_PEGGED_STABLECOINS]
        : [...EUR_PEGGED_STABLECOINS, ...USD_PEGGED_STABLECOINS]; // EUR can use both

      let sourceWallet: BridgeWallet | null = null;
      let bestSourceCurrency: string | undefined = undefined;

      // Find wallet with sufficient balance and best source currency
      for (const wallet of walletBalances) {
        // Get all compatible stablecoin balances sorted by value (highest first)
        const compatibleBalances = wallet.balances
          .filter(b => stablecoins.includes(b.currency.toUpperCase()))
          .sort((a, b) => parseFloat(b.usd_value || b.balance || '0') - parseFloat(a.usd_value || a.balance || '0'));

        const walletTotal = compatibleBalances.reduce((sum, b) => sum + parseFloat(b.usd_value || b.balance || '0'), 0);

        if (walletTotal >= parseFloat(amount)) {
          sourceWallet = wallet;
          // Use the stablecoin with the highest balance
          const topBalance = compatibleBalances[0];
          if (topBalance) {
            bestSourceCurrency = topBalance.currency.toLowerCase();
          }
          break;
        }
      }

      if (!sourceWallet) {
        toast.error(`Insufficient stablecoin balance for ${selectedCurrency} withdrawal`);
        return;
      }

      try {
        await withdrawMutation.mutateAsync({
          from_wallet_id: sourceWallet.id,
          to_external_account_id: selectedAccount.id,
          amount: amount,
          currency: selectedCurrency as 'USD' | 'EUR', // Target fiat currency
          source_currency: bestSourceCurrency, // Source stablecoin (Bridge handles conversion)
          payment_rail: selectedPaymentRail,
          // Optional reference - recipient will see this in their bank statement
          ...(bankReference.trim() && { reference: bankReference.trim() })
        });

        toast.success(`Withdrawal of ${selectedCurrency} ${amount} via ${PAYMENT_RAIL_LABELS[selectedPaymentRail].name} initiated`);
        navigate('/dashboard');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Withdrawal failed');
      }
    } else {
      // Crypto withdrawal to external wallet
      console.log('[WithdrawPage] Crypto withdrawal - checking fields:', {
        selectedNetwork,
        selectedAsset,
        walletAddress,
        amount,
        walletBalancesCount: walletBalances?.length || 0,
        walletBalances: walletBalances?.map(w => ({ id: w.id, chain: w.chain, balances: w.balances }))
      });

      if (!selectedNetwork || !selectedAsset || !walletAddress || !amount) {
        console.error('[WithdrawPage] Missing required fields:', {
          hasNetwork: !!selectedNetwork,
          hasAsset: !!selectedAsset,
          hasAddress: !!walletAddress,
          hasAmount: !!amount
        });
        toast.error('Please fill all required fields');
        return;
      }

      // Find wallet with sufficient balance for the selected asset on selected network
      const sourceWallet = walletBalances?.find(wallet => {
        // Check if wallet chain matches selected network
        console.log('[WithdrawPage] Checking wallet:', {
          walletId: wallet.id,
          walletChain: wallet.chain,
          walletChainLower: wallet.chain.toLowerCase(),
          selectedNetwork,
          chainMatch: wallet.chain.toLowerCase() === selectedNetwork
        });
        if (wallet.chain.toLowerCase() !== selectedNetwork) return false;

        // Check if wallet has sufficient balance for the asset
        const assetBalance = wallet.balances.find(
          b => b.currency.toUpperCase() === selectedAsset
        );

        if (!assetBalance) return false;
        return parseFloat(assetBalance.balance || '0') >= parseFloat(amount);
      });

      if (!sourceWallet) {
        console.error('[WithdrawPage] No source wallet found for:', { selectedNetwork, selectedAsset, amount });
        toast.error(`Insufficient ${selectedAsset} balance on ${getNetworkInfo(selectedNetwork)?.name || selectedNetwork}`);
        return;
      }

      console.log('[WithdrawPage] Source wallet found:', {
        id: sourceWallet.id,
        chain: sourceWallet.chain,
        balances: sourceWallet.balances
      });

      // Final validation before API call
      const transferPayload = {
        from_wallet_id: sourceWallet.id,
        destination_address: walletAddress.trim(),
        destination_chain: selectedNetwork,
        amount: amount,
        currency: selectedAsset.toLowerCase(),
        // Include memo for Stellar network
        ...(selectedNetwork === 'stellar' && stellarMemo && { memo: stellarMemo })
      };

      // Debug logging for troubleshooting
      console.log('[WithdrawPage] Crypto transfer payload:', transferPayload);

      // Validate all required fields are present and non-empty
      if (!transferPayload.from_wallet_id) {
        toast.error('Source wallet not selected');
        return;
      }
      if (!transferPayload.destination_address) {
        toast.error('Destination address is required');
        return;
      }
      if (!transferPayload.destination_chain) {
        toast.error('Network not selected. Please select a network.');
        return;
      }

      try {
        await cryptoTransferMutation.mutateAsync(transferPayload);

        toast.success(`Withdrawal of ${amount} ${selectedAsset} to ${walletAddress.slice(0, 8)}...${walletAddress.slice(-4)} initiated`);
        navigate('/dashboard');
      } catch (error) {
        console.error('[WithdrawPage] Crypto transfer error:', error);
        toast.error(error instanceof Error ? error.message : 'Withdrawal failed');
      }
    }
  };

  const handleAddBankAccount = () => {
    navigate('/add-bank-account');
  };

  // Currency helpers
  const getCurrencySymbol = () => {
    if (destinationType === 'crypto' && selectedAsset) {
      return ['USDC', 'USDT', 'USDB', 'DAI', 'PYUSD', 'USDP'].includes(selectedAsset) ? '$' : '€';
    }
    return selectedCurrency === 'USD' ? '$' : '€';
  };

  const getDisplayCurrency = () => {
    return destinationType === 'crypto' && selectedAsset ? selectedAsset : selectedCurrency;
  };

  // ============================================
  // RENDER STEP CONTENT
  // ============================================

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Destination Type
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className={cn(semanticTokens.typography.h2, semanticTokens.fg.default, "mb-2")}>
                Where do you want to withdraw?
              </h2>
              <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Choose between your bank account or an external crypto wallet
              </p>
            </div>

            <DestinationTypeTabs
              value={destinationType}
              onChange={setDestinationType}
            />

            {/* Explanation card */}
            <div className={cn(
              "flex items-start gap-3 p-4",
              semanticTokens.info.bg,
              semanticTokens.info.border,
              semanticTokens.radius.lg,
              "border",
              semanticTokens.motion.fadeIn
            )}>
              <SFIcon icon={sfIcons.info} className={cn("w-5 h-5 shrink-0 mt-0.5", semanticTokens.info.accent)} />
              <div>
                <p className={cn(semanticTokens.typography.label, semanticTokens.info.fg, "mb-1")}>
                  {destinationType === 'bank' ? 'Bank Withdrawal' : 'Crypto Withdrawal'}
                </p>
                <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                  {destinationType === 'bank'
                    ? 'Your stablecoins (USDC, USDT, etc.) will be converted to fiat and sent to your bank account via ACH, Wire, or SEPA.'
                    : 'Send your stablecoins directly to an external wallet on the selected network.'}
                </p>
              </div>
            </div>
          </div>
        );

      case 1: // Destination Selection
        if (destinationType === 'bank') {
          return (
            <div className="space-y-6">
              {/* Currency Selection */}
              <div>
                <Label className={cn(semanticTokens.typography.label, "mb-3 block")}>
                  Select Currency
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {CURRENCY_OPTIONS.map(currency => (
                    <Card
                      key={currency.id}
                      className={cn(
                        "cursor-pointer min-h-[72px]",
                        semanticTokens.motion.standard,
                        semanticTokens.focus.ring,
                        "hover:shadow-md",
                        selectedCurrency === currency.id
                          ? 'ring-2 ring-primary ring-offset-2 border-primary'
                          : semanticTokens.border.default,
                        semanticTokens.bg.surface,
                        semanticTokens.shadow.sm,
                        semanticTokens.radius.lg,
                        "focus-visible:outline-none"
                      )}
                      onClick={() => {
                        setSelectedCurrency(currency.id);
                        const rails = PAYMENT_RAILS[currency.id];
                        if (rails && rails.length > 0) {
                          setSelectedPaymentRail(rails[0] as PaymentRail);
                        }
                        setSelectedAccount(null);
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Select ${currency.name}`}
                      aria-pressed={selectedCurrency === currency.id}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedCurrency(currency.id);
                          const rails = PAYMENT_RAILS[currency.id];
                          if (rails && rails.length > 0) {
                            setSelectedPaymentRail(rails[0] as PaymentRail);
                          }
                          setSelectedAccount(null);
                        }
                      }}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-12 h-12",
                            semanticTokens.bg.muted,
                            semanticTokens.radius.full,
                            "flex items-center justify-center"
                          )}>
                            <CurrencyIcon symbol={currency.id} size="md" />
                          </div>
                          <div>
                            <div className={cn(semanticTokens.typography.bodyLarge, semanticTokens.fg.default, "font-medium")}>
                              {currency.id}
                            </div>
                            <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                              {currency.name}
                            </div>
                          </div>
                        </div>
                        {selectedCurrency === currency.id && (
                          <SFIcon icon={sfIcons.check} className={cn("w-5 h-5", semanticTokens.positive.accent)} />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Balance Breakdown - Shows stablecoins with Bridge API exchange rates */}
              <BalanceBreakdown
                breakdown={fiatBreakdown}
                targetCurrency={selectedCurrency}
                total={availableBalanceForFiat}
                rates={rates}
                isLoading={isLoadingRates}
              />

              {/* Payment Rail Selection */}
              <div>
                <Label className={cn(semanticTokens.typography.label, "mb-3 block")}>
                  Payment Method
                </Label>
                <div className="grid gap-3">
                  {PAYMENT_RAILS[selectedCurrency].map(rail => (
                    <PaymentRailCard
                      key={rail}
                      name={PAYMENT_RAIL_LABELS[rail].name}
                      description={PAYMENT_RAIL_LABELS[rail].description}
                      eta={PAYMENT_RAIL_LABELS[rail].eta}
                      isSelected={selectedPaymentRail === rail}
                      onClick={() => setSelectedPaymentRail(rail)}
                    />
                  ))}
                </div>
              </div>

              {/* Bank Account Selection */}
              <div>
                <Label className={cn(semanticTokens.typography.label, "mb-3 block")}>
                  Bank Account
                </Label>

                {isLoadingAccounts ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className={cn("h-20 w-full", semanticTokens.radius.lg)} />
                    ))}
                  </div>
                ) : !Array.isArray(externalAccounts) || externalAccounts.length === 0 ? (
                  <EmptyState
                    icon={CreditCard}
                    title="No connected bank accounts"
                    description="Please add a bank account to withdraw funds."
                    action={{
                      label: "Add Bank Account",
                      onClick: handleAddBankAccount
                    }}
                  />
                ) : filteredAccounts.length === 0 ? (
                  <EmptyState
                    icon={CreditCard}
                    title={`No ${selectedCurrency} accounts`}
                    description={`Add a ${selectedCurrency === 'USD' ? 'US bank' : 'European'} account to withdraw ${selectedCurrency}.`}
                    action={{
                      label: "Add Bank Account",
                      onClick: handleAddBankAccount
                    }}
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredAccounts.map((account: ExternalAccount) => (
                      <AccountSelectionCard
                        key={account.id}
                        account={account}
                        isSelected={selectedAccount?.id === account.id}
                        onClick={() => setSelectedAccount(account)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        } else {
          // Crypto withdrawal
          return (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <h3 className={cn(semanticTokens.typography.h3, semanticTokens.fg.default, "mb-2")}>
                  Select Network & Asset
                </h3>
                <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                  Choose the blockchain network and asset for your withdrawal
                </p>
              </div>

              <NetworkAssetSelector
                network={selectedNetwork}
                asset={selectedAsset}
                onNetworkChange={setSelectedNetwork}
                onAssetChange={setSelectedAsset}
              />

              {/* Show balance for selected asset/network */}
              {selectedNetwork && selectedAsset && (
                <>
                  <div className={cn(
                    "flex items-center justify-between p-3",
                    semanticTokens.bg.muted,
                    semanticTokens.radius.lg
                  )}>
                    <div className="flex items-center gap-2">
                      <CurrencyIcon symbol={selectedAsset} size="sm" />
                      <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                        {selectedAsset} on {getNetworkInfo(selectedNetwork)?.name}
                      </span>
                    </div>
                    <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                      {availableBalanceForCrypto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} {selectedAsset}
                    </span>
                  </div>

                  <NewWalletEntry
                    chain={selectedNetwork}
                    currency={selectedAsset}
                    address={walletAddress}
                    onAddressChange={setWalletAddress}
                    isValid={isAddressValid}
                    validationMessage={
                      walletAddress
                        ? isAddressValid
                          ? 'Valid address format'
                          : 'Invalid address format for selected network'
                        : undefined
                    }
                  />

                  {/* Stellar Memo Field - Required for Stellar network */}
                  {selectedNetwork === 'stellar' && (
                    <div className={cn("space-y-2", semanticTokens.motion.standard)}>
                      <Label htmlFor="stellar-memo" className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                        Stellar Memo <span className={semanticTokens.danger.fg}>*</span>
                      </Label>
                      <Input
                        id="stellar-memo"
                        placeholder="Enter memo (up to 28 characters)"
                        maxLength={28}
                        value={stellarMemo}
                        onChange={(e) => setStellarMemo(e.target.value)}
                        className={cn(
                          stellarMemoError && semanticTokens.danger.border,
                          "focus-visible:ring-2 focus-visible:ring-blue-500",
                          semanticTokens.motion.standard
                        )}
                        aria-invalid={!!stellarMemoError}
                        aria-describedby={stellarMemoError ? 'stellar-memo-error' : 'stellar-memo-help'}
                      />
                      {stellarMemoError ? (
                        <p id="stellar-memo-error" className={cn(semanticTokens.typography.caption, semanticTokens.danger.fg)} role="alert">
                          {stellarMemoError}
                        </p>
                      ) : (
                        <p id="stellar-memo-help" className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                          Required for Stellar network. This memo ensures your withdrawal is correctly processed by the recipient.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }

      case 2: // Amount
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className={cn(semanticTokens.typography.h2, semanticTokens.fg.default, "mb-2")}>
                How much do you want to withdraw?
              </h2>
              <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Enter the amount you'd like to send
              </p>
            </div>

            {/* Available Balance Card */}
            <Card className={cn(semanticTokens.motion.standard, "overflow-hidden")}>
              <CardContent className={cn(semanticTokens.spacing.md)}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Wallet className={cn("w-5 h-5", semanticTokens.fg.muted)} />
                    <span className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                      Available Balance
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CurrencyIcon symbol={getDisplayCurrency()} size="sm" />
                    <span className={cn(semanticTokens.typography.h3, semanticTokens.fg.default)}>
                      {getCurrencySymbol()}{availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Amount Input */}
            <AmountInput
              value={amount}
              onChange={setAmount}
              currency={getDisplayCurrency()}
              maxAmount={availableBalance}
              minAmount={minimumWithdrawAmount}
            />

            {/* Quick Amount Buttons */}
            <div className="flex gap-2 flex-wrap">
              {[25, 50, 75, 100].map(percent => {
                const quickAmount = (availableBalance * (percent / 100)).toFixed(2);
                return (
                  <Button
                    key={percent}
                    variant="outline"
                    size="sm"
                    onClick={() => setAmount(quickAmount)}
                    className={cn("flex-1 min-w-[60px]", semanticTokens.motion.standard)}
                    disabled={availableBalance <= 0}
                  >
                    {percent}%
                  </Button>
                );
              })}
            </div>

            {/* Fee Preview */}
            {amount && parseFloat(amount) > 0 && (
              <WithdrawSummaryInline
                amount={parseFloat(amount)}
                currency={getDisplayCurrency()}
                fee={fees.totalFee}
                youReceive={fees.youReceive}
                isHighFee={fees.isHighFee}
                isCriticalFee={fees.isCriticalFee}
                suggestedNetwork={fees.suggestedNetwork}
                potentialSavings={fees.potentialSavings}
              />
            )}
          </div>
        );

      case 3: // Review
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className={cn(semanticTokens.typography.h2, semanticTokens.fg.default, "mb-2")}>
                Review your withdrawal
              </h2>
              <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
                Please verify all details before confirming
              </p>
            </div>

            <WithdrawSummaryCard
              destinationType={destinationType}
              amount={parseFloat(amount)}
              currency={getDisplayCurrency()}
              fees={{
                amount: parseFloat(amount),
                networkFee: fees.networkFee > 0 ? fees.networkFee : undefined,
                serviceFee: fees.developerFee,
                railFee: fees.railFee > 0 ? fees.railFee : undefined,
                totalFee: fees.totalFee,
                youReceive: fees.youReceive,
                feeDetails: fees.feeDetails,
              }}
              paymentRail={destinationType === 'bank' ? selectedPaymentRail : undefined}
              bankName={selectedAccount?.bank_name}
              accountLast4={selectedAccount?.account_last4}
              estimatedArrival={destinationType === 'bank'
                ? getEstimatedArrival(selectedPaymentRail, 'bank')
                : getEstimatedArrival(selectedNetwork || '', 'crypto')
              }
              chain={selectedNetwork || undefined}
              walletAddress={walletAddress}
              walletAddressPreview={walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : undefined}
              isConfirming={withdrawMutation.isPending || cryptoTransferMutation.isPending}
            />

            {/* Optional Reference for Bank Withdrawals */}
            {destinationType === 'bank' && (
              <div className={cn("space-y-2", semanticTokens.motion.standard)}>
                <Label htmlFor="bank-reference" className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                  Payment Reference <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>(optional)</span>
                </Label>
                <Input
                  id="bank-reference"
                  placeholder="e.g., Invoice INV-12345, Order ID, etc."
                  maxLength={140}
                  value={bankReference}
                  onChange={(e) => setBankReference(e.target.value)}
                  className={cn(
                    semanticTokens.focus.ring,
                    semanticTokens.motion.standard
                  )}
                  aria-describedby="bank-reference-help"
                />
                <p id="bank-reference-help" className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                  This reference will appear in the recipient's bank statement. Useful for tracking payments or invoices.
                </p>
              </div>
            )}

            {destinationType === 'crypto' && (
              <Alert className={cn(semanticTokens.warning.bg, semanticTokens.warning.border)}>
                <AlertTriangle className={cn("h-4 w-4", semanticTokens.warning.accent)} />
                <AlertDescription className={semanticTokens.warning.fg}>
                  <strong>Important:</strong> Crypto transactions are irreversible. Please verify the wallet address and network are correct.
                </AlertDescription>
              </Alert>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <FormPage
      icon={ArrowDown}
      title="Withdraw Funds"
      subtitle={destinationType === 'bank'
        ? "Convert stablecoins to fiat and transfer to your bank"
        : "Send crypto to an external wallet"}
      returnPath="/dashboard"
    >
      <div className={cn("max-w-2xl mx-auto", semanticTokens.motion.fadeIn)}>
        <StepIndicator steps={STEPS} currentStep={currentStep} />

        <div className={cn("mb-8", semanticTokens.motion.standard)}>
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className={cn("flex gap-3 pt-6", semanticTokens.border.default, "border-t")}>
          {currentStep > 0 && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={withdrawMutation.isPending}
              className={cn("flex-1 min-h-[48px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            >
              Back
            </Button>
          )}

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!isStepValid(currentStep)}
              className={cn("flex-1 min-h-[48px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            >
              Continue
              <SFIcon icon={sfIcons.next} className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleWithdraw}
              disabled={withdrawMutation.isPending || !isStepValid(currentStep - 1)}
              className={cn("flex-1 min-h-[48px]", semanticTokens.motion.standard, semanticTokens.focus.ring, "bg-primary hover:bg-primary/90")}
            >
              {withdrawMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Confirm Withdrawal
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </FormPage>
  );
}
